import { NameColumn } from '@/components/NameColumn';
import { getColumnWidth } from '@/components/table/columns/registry';
import type { Columns } from '@/components/table/types';
import { AssetStatusComponent } from '@/sections/asset/components/badges/AssetStatus';
import type { AWSResource } from '@/types';

const STATUS_COLUMN_WIDTH = 100;

/**
 * Builds column definitions for AWSResource entity tables
 *
 * Returns columns for:
 * - status: Asset status indicator
 * - resourceType: Type of AWS resource (EC2, S3, etc.)
 * - name: Display name with identifier below
 * - created: First seen timestamp
 * - visited: Last seen timestamp
 *
 * @returns Column definitions for TanStack Table
 */
export function buildAWSResourceColumns(): Columns<AWSResource> {
  return [
    {
      id: 'status',
      label: 'Status',
      fixedWidth: STATUS_COLUMN_WIDTH,
      minWidth: STATUS_COLUMN_WIDTH,
      maxWidth: STATUS_COLUMN_WIDTH,
      cell: (item: AWSResource) =>
        item?.status ? <AssetStatusComponent asset={item} /> : '-',
    },
    {
      id: 'resourceType',
      label: 'Resource Type',
      cell: (item: AWSResource) => item?.resourceType || '-',
    },
    {
      id: 'displayName',
      label: 'Name',
      maxWidth: 350,
      cell: (item: AWSResource) => {
        if (!item?.displayName) return '-';
        return (
          <NameColumn
            name={item.displayName}
            description={item.identifier || item.name}
          />
        );
      },
    },
    {
      id: 'created',
      label: 'First Seen',
      ...getColumnWidth('shared', 'created'),
      cell: 'date' as const,
    },
    {
      id: 'visited',
      label: 'Last Seen',
      ...getColumnWidth('shared', 'visited'),
      cell: 'date' as const,
    },
  ];
}
